import { Routes, Route, Link, useLocation } from "react-router-dom";
import { LogOut, MessageSquare, BarChart3, Activity, Users, Menu, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import AgentChat from "./AgentChat";
import ConfigurationPanel from "./ConfigurationPanel";
import EvaluationDashboard from "./EvaluationDashboard";
import UserManagement from "./UserManagement";
import { ErrorBoundary } from "./ErrorBoundary";
import { useAuth } from "../context/AuthContext";
import Login from "./Login";
import ChangePasswordScreen from "./ChangePasswordScreen";

export default function AppLayout() {
  const { user, loading, signOut, mustChangePassword } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isAdmin = user?.role === 'admin';

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-background via-background to-orange-950/20">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login />;
  }

  // Force password change for temp-password users
  if (mustChangePassword) {
    return <ChangePasswordScreen />;
  }

  return (
    <div className="flex h-screen flex-col bg-linear-to-br from-background via-background to-orange-950/20 overflow-hidden">
      {/* Header */}
      <header className="border-b border-primary/30 glass-header shrink-0 relative z-200">
        <div className="px-6 py-4">
          <div className="grid grid-cols-3 items-center max-w-full">
            {/* Left: Burger menu */}
            <div className="flex items-center gap-3" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center justify-center h-10 w-10 rounded-lg hover:bg-primary/10 transition-colors"
                aria-label="Navigation menu"
              >
                {menuOpen ? (
                  <X className="h-6 w-6 text-muted-foreground" />
                ) : (
                  <Menu className="h-6 w-6 text-muted-foreground" />
                )}
              </button>

              {/* Dropdown menu */}
              {menuOpen && (
                <div className="absolute left-6 top-full mt-1 w-56 py-2 rounded-lg bg-background border border-primary/30 shadow-lg z-50">
                  <Link
                    to="/"
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      location.pathname === '/'
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <img src="/assets/logo.png" alt="" className="h-5 w-5 rounded object-contain" />
                    Home
                  </Link>
                  <Link
                    to="/chat"
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      location.pathname === '/chat'
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <MessageSquare className="h-5 w-5" />
                    Chat
                  </Link>
                  <Link
                    to="/evaluation"
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      location.pathname === '/evaluation'
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <Activity className="h-5 w-5" />
                    Evaluate
                  </Link>
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      location.pathname === '/dashboard'
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                    }`}
                  >
                    <BarChart3 className="h-5 w-5" />
                    Results
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/users"
                      className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                        location.pathname === '/users'
                          ? 'bg-primary/20 text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                      }`}
                    >
                      <Users className="h-5 w-5" />
                      Users
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Center: Title */}
            <div className="flex justify-center">
              <Link to="/" className="text-center hover:opacity-80 transition-opacity">
                <h1 className="text-2xl font-bold bg-linear-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent whitespace-nowrap">
                  Roamler Data Assistant
                </h1>
              </Link>
            </div>

            {/* Right: User Avatar */}
            <div className="flex items-center justify-end">
              <div className="relative group">
                <button
                  className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/20 border-2 border-primary/40 hover:border-primary/80 hover:bg-primary/30 transition-all duration-200 cursor-pointer"
                  title={user.displayName || user.email || 'User'}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || user.email || 'User'}
                      className="h-full w-full rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-primary">
                      {(() => {
                        const name = user.displayName || user.email || '';
                        const parts = name.split(/[@\s]+/);
                        if (parts.length >= 2 && parts[0] && parts[1]) {
                          return (parts[0][0] + parts[1][0]).toUpperCase();
                        }
                        return name.slice(0, 2).toUpperCase();
                      })()}
                    </span>
                  )}
                </button>
                {/* Dropdown */}
                <div className="absolute right-0 top-full mt-2 w-48 py-2 rounded-lg bg-card border border-primary/30 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="px-3 py-2 border-b border-primary/20">
                    <p className="text-sm font-medium text-foreground truncate">{user.displayName || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    {user.role && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary/15 text-primary border border-primary/30">
                        {user.role}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => signOut()}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden flex">
        <ErrorBoundary>
        <Routes>
          {/* Home Route — cards */}
          <Route path="/" element={
            <div className="flex-1 overflow-y-auto bg-linear-to-br from-background via-background to-primary/5 flex items-center justify-center p-8">
              <div className="w-full max-w-5xl">
                <div className="text-center mb-12">
                  <p className="text-xl text-muted-foreground">
                    Ask questions about your data in natural language
                  </p>
                </div>

                <div className={`grid grid-cols-1 gap-6 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                  {/* Chat */}
                  <Link
                    to="/chat"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-linear-to-br from-orange-500/20 via-amber-500/10 to-yellow-500/15 border-2 border-orange-500/40 hover:border-orange-500/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-orange-500/40"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-orange-500/25 to-amber-500/25 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-orange-500/25 group-hover:bg-orange-500/40 transition-colors">
                          <MessageSquare className="h-12 w-12 text-orange-400" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Chat</h2>
                      <p className="text-muted-foreground text-center">
                        Ask questions and get answers from your data
                      </p>
                    </div>
                  </Link>

                  {/* Start Evaluation */}
                  <Link
                    to="/evaluation"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-linear-to-br from-green-500/20 via-emerald-500/10 to-teal-500/20 border-2 border-green-500/40 hover:border-green-500/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-green-500/40"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-green-500/30 to-teal-500/30 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-green-500/30 group-hover:bg-green-500/50 transition-colors">
                          <Activity className="h-12 w-12 text-green-400" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Start Evaluation</h2>
                      <p className="text-muted-foreground text-center">
                        Run the agent against test datasets
                      </p>
                    </div>
                  </Link>

                  {/* Evaluation Results */}
                  <Link
                    to="/dashboard"
                    className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-linear-to-br from-primary/20 via-orange-500/10 to-amber-500/15 border-2 border-primary/40 hover:border-primary/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-orange-500/40"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-primary/25 to-amber-500/25 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                    <div className="relative">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 rounded-full bg-primary/30 group-hover:bg-primary/50 transition-colors">
                          <BarChart3 className="h-12 w-12 text-primary" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-2 text-center">Evaluation Results</h2>
                      <p className="text-muted-foreground text-center">
                        View results and analyze performance
                      </p>
                    </div>
                  </Link>

                  {/* User Management — admin only */}
                  {isAdmin && (
                    <Link
                      to="/users"
                      className="group relative p-8 rounded-2xl backdrop-blur-2xl bg-linear-to-br from-violet-500/20 via-purple-500/10 to-indigo-500/15 border-2 border-violet-500/40 hover:border-violet-500/80 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-violet-500/40"
                    >
                      <div className="absolute inset-0 rounded-2xl bg-linear-to-br from-violet-500/25 to-indigo-500/25 blur-xl opacity-0 group-hover:opacity-60 transition-opacity" />
                      <div className="relative">
                        <div className="flex justify-center mb-4">
                          <div className="p-4 rounded-full bg-violet-500/25 group-hover:bg-violet-500/40 transition-colors">
                            <Users className="h-12 w-12 text-violet-400" />
                          </div>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground mb-2 text-center">User Management</h2>
                        <p className="text-muted-foreground text-center">
                          Manage users, roles, and access
                        </p>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          } />

          {/* Chat Route */}
          <Route path="/chat" element={
            <div className="flex-1 overflow-hidden">
              <AgentChat />
            </div>
          } />

          {/* Evaluation Configuration Route */}
          <Route path="/evaluation" element={
            <div className="flex-1 overflow-y-auto bg-background">
              <ConfigurationPanel />
            </div>
          } />

          {/* Evaluation Dashboard Route */}
          <Route path="/dashboard" element={
            <div className="flex-1 overflow-y-auto bg-background">
              <EvaluationDashboard />
            </div>
          } />

          {/* User Management Route — admin only */}
          <Route path="/users" element={
            <div className="flex-1 overflow-y-auto bg-background">
              <UserManagement />
            </div>
          } />

          {/* 404 Not Found */}
          <Route path="*" element={
            <div className="flex-1 flex items-center justify-center bg-linear-to-br from-background via-background to-primary/5">
              <div className="text-center">
                <h2 className="text-4xl font-bold text-muted-foreground mb-2">404</h2>
                <p className="text-muted-foreground mb-4">Page not found</p>
                <Link to="/" className="text-primary hover:text-primary/80 transition-colors">
                  Go home
                </Link>
              </div>
            </div>
          } />
        </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}
